import { Gemini } from "@lobehub/icons";
import { Button } from "@promptwatch/ui/components/button";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@promptwatch/ui/components/hover-card";
import { cn } from "@promptwatch/ui/lib/utils";
import { Braces, Bug, Gauge, InfoIcon, SquarePen, Users } from "lucide-react";
import Image from "next/image";
import { useRouter } from "next/router";
import { usePostHog } from "posthog-js/react";
import type React from "react";
import { clientEnv } from "@/env/client";

interface Feature {
  text: string;
  explainer: string;
  available: boolean;
  icon?: React.ComponentType<{ className?: string }> | (() => React.ReactNode);
}

interface PricingTier {
  name: string;
  description: string;
  price: string;
  period: string;
  features: Feature[];
  buttonText: string;
  buttonVariant: string;
  highlight: boolean;
}

const pricingTiers: PricingTier[] = [
  {
    name: "Essential",
    description: "Get started with basic monitoring and analytics",
    price: "$89",
    period: "/month",
    features: [
      {
        text: "1 website",
        explainer: "Track a single website",
        available: true,
      },
      {
        text: "2 monitors",
        explainer: "Monitors are collections of prompts you want to track",
        available: true,
      },
      {
        text: "50 prompts / 800 responses",
        explainer: "Number of prompts you can monitor simultaneously",
        available: true,
      },
      {
        text: "Weekly refresh",
        explainer: "Data is refreshed weekly",
        available: true,
      },
      {
        text: "200K visitor analytics",
        explainer: "Number of visits you can track per month",
        available: true,
      },
      {
        text: "Content optimization",
        explainer: "AI-powered suggestions to improve your content for better visibility in AI search results",
        available: true,
        icon: () => <SquarePen className="size-[14px] -ml-0.5" />,
      },
      {
        text: "Technical optimization",
        explainer: "Technical recommendations to optimize your website structure for AI crawlers",
        available: true,
        icon: () => <Gauge className="size-[14px] -ml-0.5" />,
      },
      {
        text: "Crawler analytics",
        explainer: "See exactly what AI crawlers are visiting your website and what pages",
        available: false,
        icon: () => <Bug className="size-[14px] -ml-0.5" />,
      },
      {
        text: "Multiple seats",
        explainer: "One user account with full access",
        available: false,
        icon: () => <Users className="size-[14px] -ml-0.5" />,
      },
      {
        text: "API access",
        explainer: "Access to our API for custom integrations",
        available: false,
        icon: () => <Braces className="size-[14px] -ml-0.5" />,
      },
      {
        text: "Looker studio integration",
        explainer: "View your data in Looker studio",
        available: false,
        icon: () => <Image src="/looker.svg" alt="Looker studio" width={12} height={12} />,
      },
    ],
    buttonText: "Start 7-day Trial",
    buttonVariant: "outline",
    highlight: false,
  },
  {
    name: "Professional",
    description: "Get started with basic monitoring and analytics",
    price: "$199",
    period: "/month",
    features: [
      {
        text: "2 websites",
        explainer: "Track up to 2 websites",
        available: true,
      },
      {
        text: "5 monitors",
        explainer: "Monitors are collections of prompts you want to track",
        available: true,
      },
      {
        text: "150 prompts / 18,000 responses",
        explainer: "Number of prompts you can monitor simultaneously",
        available: true,
      },
      {
        text: "Daily refresh",
        explainer: "Data is refreshed every day to ensure you have the most up-to-date information",
        available: true,
      },
      {
        text: "1M visitor analytics",
        explainer: "Number of visits you can track per month",
        available: true,
      },
      {
        text: "Content optimization",
        explainer: "AI-powered suggestions to improve your content for better visibility in AI search results",
        available: true,
        icon: () => <SquarePen className="size-[14px] -ml-0.5" />,
      },
      {
        text: "Technical optimization",
        explainer: "Technical recommendations to optimize your website structure for AI crawlers",
        available: true,
        icon: () => <Gauge className="size-[14px] -ml-0.5" />,
      },

      {
        text: "Crawler analytics",
        explainer: "See exactly what AI crawlers are visiting your website and what pages",
        available: true,
        icon: () => <Bug className="size-[14px] -ml-0.5" />,
      },
      {
        text: "2 seats",
        explainer: "Two user accounts with full access",
        available: true,
        icon: () => <Users className="size-[14px] -ml-0.5" />,
      },
      {
        text: "API access",
        explainer: "Access to our API for custom integrations",
        available: true,
        icon: () => <Braces className="size-[14px] -ml-0.5" />,
      },
      {
        text: "Looker studio integration",
        explainer: "View your data in Looker studio",
        available: true,
        icon: () => <Image src="/looker.svg" alt="Looker studio" width={12} height={12} />,
      },
    ],
    buttonText: "Start 7-day Trial",
    buttonVariant: "default",
    highlight: true,
  },
  {
    name: "Business",
    description: "Advanced features for growing businesses",
    price: "$499",
    period: "/month",
    features: [
      {
        text: "5 websites",
        explainer: "Track up to 5 websites",
        available: true,
      },
      {
        text: "15 monitors",
        explainer: "Monitors are collections of prompts you want to track",
        available: true,
      },
      {
        text: "350 prompts / 42,000 responses",
        explainer: "Number of prompts you can monitor simultaneously",
        available: true,
      },
      {
        text: "Daily refresh",
        explainer: "Data is refreshed every day to ensure you have the most up-to-date information",
        available: true,
      },
      {
        text: "10M visitor analytics",
        explainer: "Number of visits you can track per month",
        available: true,
      },
      {
        text: "Content optimization",
        explainer: "AI-powered suggestions to improve your content for better visibility in AI search results",
        available: true,
        icon: () => <SquarePen className="size-[14px] -ml-0.5" />,
      },
      {
        text: "Technical optimization",
        explainer: "Technical recommendations to optimize your website structure for AI crawlers",
        available: true,
        icon: () => <Gauge className="size-[14px] -ml-0.5" />,
      },

      {
        text: "Crawler analytics",
        explainer: "See exactly what AI crawlers are visiting your website and what pages",
        available: true,
        icon: () => <Bug className="size-[14px] -ml-0.5" />,
      },
      {
        text: "5 seats",
        explainer: "Five user accounts with full access",
        available: true,
        icon: () => <Users className="size-[14px] -ml-0.5" />,
      },
      {
        text: "API access",
        explainer: "Access to our API for custom integrations",
        available: true,
        icon: () => <Braces className="size-[14px] -ml-0.5" />,
      },
      {
        text: "Looker studio integration",
        explainer: "View your data in Looker studio",
        available: true,
        icon: () => <Image src="/looker.svg" alt="Looker studio" width={12} height={12} />,
      },
    ],
    buttonText: "Start 7-day Trial",
    buttonVariant: "outline",
    highlight: false,
  },
];

interface PricingSectionProps {
  showHeaderText?: boolean;
}

const PricingSection = ({ showHeaderText = true }: PricingSectionProps = {}) => {
  const posthog = usePostHog();
  const router = useRouter();

  return (
    <div className="">
      <section id="pricing" className={cn("relative lg:mx-px lg:rounded-[2rem]", showHeaderText ? "pt-20" : "")}>
        {showHeaderText && (
          <div className="mx-auto mb-16 grid max-w-6xl gap-6 px-12 md:grid-cols-2 lg:gap-0">
            <h2 className="text-3xl font-semibold md:text-4xl">
              Start increasing your brand visibility in <span className="text-muted-foreground">AI search</span>
            </h2>

            <div className="max-w-md text-muted-foreground">
              <p>
                Find the plan that best suits your monitoring goals.{" "}
                <span className="font-medium text-foreground">All plans include core features</span> and come with varying limits and
                capabilities to help boost your brand's visibility across AI platforms.
              </p>
            </div>
          </div>
        )}

        <div className="border-y">
          <div className="relative mx-auto max-w-6xl border-x px-4 sm:px-6 md:px-12">
            <div aria-hidden="true" className="absolute -left-1 -top-1 size-2 border bg-background shadow-sm" />
            <div aria-hidden="true" className="absolute -top-1 left-3 size-2 border bg-background shadow-sm sm:left-5 lg:left-11" />
            <div aria-hidden="true" className="absolute -right-1 -top-1 size-2 border bg-background shadow-sm" />
            <div aria-hidden="true" className="absolute -top-1 right-3 size-2 border bg-background shadow-sm sm:right-5 lg:right-11" />
            <div aria-hidden="true" className="absolute -bottom-1 -left-1 size-2 border bg-background shadow-sm" />
            <div aria-hidden="true" className="absolute -bottom-1 left-3 size-2 border bg-background shadow-sm sm:left-5 lg:left-11" />
            <div aria-hidden="true" className="absolute -bottom-1 -right-1 size-2 border bg-background shadow-sm" />
            <div aria-hidden="true" className="absolute -bottom-1 right-3 size-2 border bg-background shadow-sm sm:right-5 lg:right-11" />

            <div className="relative grid divide-y border-x  lg:grid-cols-3 lg:divide-x lg:divide-y-0">
              {pricingTiers.map((tier, _index) => (
                <div
                  key={tier.name}
                  className={`relative p-6 ${
                    tier.highlight
                      ? "bg-white hover:!bg-ui relative p-8 shadow-lg shadow-gray-900/5 before:absolute before:inset-1 before:rounded-b-lg before:bg-gradient-to-t before:from-blue-200 group-hover:bg-transparent xl:p-8"
                      : "bg-background"
                  } transition-colors duration-300 sm:p-8 lg:p-8`}
                >
                  {tier.highlight && (
                    <div className="absolute -top-3 -right-3 z-10">
                      <div className="bg-gray-600 text-white text-xs font-medium px-3 py-1 rounded-full shadow-lg">Most popular</div>
                    </div>
                  )}
                  <div className="relative flex flex-col h-full">
                    <div className="space-y-6">
                      <h3 className="relative text-lg font-medium">{tier.name}</h3>
                      <p className="relative mt-4 text-muted-foreground">{tier.description}</p>
                      <div className="mt-8">
                        <span className="text-3xl font-bold">{tier.price}</span>
                        <span className="text-muted-foreground">{tier.period}</span>
                      </div>
                      <ul className="mt-8 space-y-3">
                        {tier.features.map((feature, featureIndex) => (
                          <li key={featureIndex} className={`flex items-center gap-2 ${!feature.available ? "opacity-50" : ""}`}>
                            {feature.icon ? (
                              typeof feature.icon === "function" && feature.icon.length === 0 ? (
                                <feature.icon />
                              ) : (
                                <feature.icon className={`size-3 ${feature.available ? "text-blue-600/80" : "text-gray-400"}`} />
                              )
                            ) : feature.text.includes("AI overview") ? (
                              <Gemini.Color className={`size-3 ${feature.available ? "text-gray-900" : "text-gray-400"}`} />
                            ) : (
                              <Gemini className={`size-3 ${feature.available ? "text-blue-600/80" : "text-gray-400"}`} />
                            )}
                            {feature.explainer && feature.available ? (
                              <HoverCard>
                                <HoverCardTrigger>
                                  <span className="border-b border-dashed border-gray-300/80 cursor-help">{feature.text}</span>
                                </HoverCardTrigger>
                                <HoverCardContent className="w-80 p-3">
                                  <InfoIcon className="absolute top-2 right-2 size-3 text-muted-foreground" />
                                  <p className="text-sm text-foreground mb-1">{feature.explainer}</p>
                                </HoverCardContent>
                              </HoverCard>
                            ) : (
                              <span className={feature.available ? "" : "text-gray-400 line-through"}>{feature.text}</span>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="mt-auto pt-8">
                      <Button
                        className="w-full"
                        onClick={() => {
                          posthog.capture("pricing_button_clicked", {
                            tier: tier.name,
                          });

                          router.push(clientEnv.NEXT_PUBLIC_APP_URL);
                        }}
                        variant={tier.buttonVariant as "outline" | "default" | "link" | "destructive" | "secondary" | "ghost"}
                      >
                        {tier.buttonText}
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="relative border-b border-gray-950/[0.07]">
        <div className="relative mx-auto max-w-6xl border-x border-gray-950/[0.07] px-4 sm:px-6 md:px-12 ">
          <div aria-hidden="true" className="bg-ui absolute -bottom-1 -left-1 size-2 border shadow-sm" />
          <div aria-hidden="true" className="bg-ui absolute -bottom-1 left-3 size-2 border shadow-sm sm:left-5 md:left-11" />
          <div aria-hidden="true" className="bg-ui absolute -bottom-1 -right-1 size-2 border shadow-sm" />
          <div aria-hidden="true" className="bg-ui absolute -bottom-1 right-3 size-2 border shadow-sm sm:right-5 md:right-11" />
          <div className="px-5 sm:px-20 border-x  border-gray-950/[0.07]  relative p-8 shadow-lg shadow-gray-900/5 before:absolute before:inset-1 before:rounded-b-lg before:bg-gradient-to-b before:from-primary/90 before:to-blue-600/70 group-hover:bg-transparent">
            <div className="flex flex-col md:flex-row items-center justify-between text-white">
              <div className="mb-6 md:mb-0 z-40">
                <h3 className="text-lg font-semibold mb-1">Custom pricing for Agencies and Enterprises</h3>
                <p className="text-gray-50/90 max-w-2xl">
                  Contact our team for custom pricing tailored to your organization. Get dedicated support, advanced features, and
                  enterprise-grade security for AI platform visibility.
                </p>
              </div>
              <div className="flex flex-row gap-4 z-40">
                <Button
                  variant="outline"
                  className="bg-white text-black"
                  onClick={() => {
                    posthog.capture("book_demo_button_clicked", {
                      source: "pricing",
                    });

                    router.push("/book-a-demo");
                  }}
                >
                  Book a Call
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PricingSection;
